import React, { ComponentType, useMemo } from 'react';
import { styled, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash';
import useDataSheetDialogActions from 'common/hooks/useDataSheetDialogActions';
import { WithTooltipProps } from './withTooltip.props';

const PREFIX = 'withTooltip';

const classes = {
  tooltip: `${PREFIX}-tooltip`,
};

const StyledWrappedComponent = styled('div')(() => ({
  [`& .${classes.tooltip}`]: {
    textAlign: 'left',
    maxWidth: '200px',
  },
}));

// Styles for the wrapped component
const styles = {
  width: '100%',
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
};

// Memoized function to determine whether the tooltip should be displayed
const shouldShowTooltip = (key?: string, tooltipMessages?: string[] | null, dialogs?: any[], alias?: string) =>
  !isEmpty(tooltipMessages) && !dialogs?.some(({ cellId, alias: cellAlias }) => cellId === key && cellAlias === alias);

const withTooltip = (WrappedComponent: ComponentType<any>) => {
  const WithTooltip = (props: WithTooltipProps) => {
    const { cell } = props;
    const { alias, key, tooltipMessages = [] } = cell;

    // Styles for the tooltip label

    const { dialogs = [] } = useDataSheetDialogActions();

    const shouldRenderTooltip = shouldShowTooltip(key, tooltipMessages, dialogs, alias);
    const title = shouldRenderTooltip ? tooltipMessages?.join(' ') : null;

    // Memoize the tooltip placement to prevent unnecessary recalculations
    const tooltipPlacement = useMemo(() => cell?.tooltipPlacement ?? 'top', [cell?.tooltipPlacement]);

    const popperOptions = useMemo(() => (cell.positionFixed ? { positionFixed: true } : {}), [cell.positionFixed]);

    const disableHoverEvent = useMemo(
      () => dialogs?.some(({ cellId = null, alias: cellAlias = null }) => cellId === key && cellAlias === alias),
      [alias, dialogs, key]
    );

    if (shouldRenderTooltip) {
      return (
        <Tooltip
          arrow
          classes={{ tooltip: classes.tooltip }}
          disableHoverListener={disableHoverEvent}
          disableTouchListener={disableHoverEvent}
          placement={tooltipPlacement}
          PopperProps={{
            disablePortal: !!cell.insideLedger,
            popperOptions,
          }}
          title={title ?? ''}>
          <div style={styles}>
            <div className="tooltip-indicator" />
            <WrappedComponent {...props} />
          </div>
        </Tooltip>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return WithTooltip;
};

export default withTooltip;
